import { first, isEmpty, isNil } from 'lodash';
import { CSSProperties } from 'react';
import { View } from '@shared/components/View';
import { Field } from '@shared/data/entity/fields/Field';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { FieldRecord } from '../../../types';
import { LookupEditField } from './LookupEditField';
import { RelatedLookupFieldOverflowMenu } from './RelatedLookupFieldOverflowMenu';
import { toEntityAndField } from './toEntityAndField';
import { EntityAndField } from './types';
import { useRelatedCardLookupFields } from './useRelatedCardLookupFields';
import styles from './RelatedCardLookupEditField.module.css';

interface Props {
  field: Field | null;
  fields: FieldRecord;
  workflowTemplateId: string;
  component: API.RelatedCardLookupComponent;
  error?: string;
  style?: CSSProperties;
}

export function RelatedCardLookupEditField({
  fields,
  component,
  error,
  workflowTemplateId,
  style,
}: Props) {
  const { leafComponent, leafEntities } = useRelatedCardLookupFields({
    component,
    fields,
    workflowTemplateId,
  });

  if (isEmpty(leafEntities) || isNil(leafComponent)) {
    return null;
  }

  const lookupFields: EntityAndField[] = leafEntities
    .map(leafEntity => toEntityAndField(leafEntity, leafComponent))
    .filter(notNil);

  const firstField = first(lookupFields);
  const count = lookupFields.length;
  const hasMore = count > 1;
  const label = `${getComponentLabel(component)} (${count})`;
  const extraCount = count - 1;

  if (isNil(firstField)) {
    return null;
  }

  return (
    <View className={[styles.relatedCardLookupEditField, hasMore && styles.hasMore]}>
      <LookupEditField
        field={firstField?.field}
        entity={firstField.entity}
        sources={firstField.sources}
        lookupComponent={leafComponent}
        label={label}
        error={error}
        workflowTemplateId={workflowTemplateId}
        style={style}
        footer={
          hasMore && (
            <View row marginLeft={-1.5} paddingVertical={0.5}>
              <RelatedLookupFieldOverflowMenu
                lookupFields={lookupFields}
                lookupComponent={leafComponent}
                workflowTemplateId={workflowTemplateId}
                extraCount={extraCount}
                title={label}
              />
            </View>
          )
        }
      />
    </View>
  );
}
